import { stageMeta } from './estadosPresupuesto';
import { primaryActionFor } from './accionesPresupuesto';

/**
 * Fuente única del "qué significa esto y qué hago ahora" para las tres entidades
 * del ciclo hallazgo → presupuesto → ODT. Los presupuestos y las ODT modelan
 * su próximo paso en `primaryActionFor` (assets/data/accionesPresupuesto);
 * este módulo lo complementa con la etapa del hallazgo y expone una API única
 * (`nextActionFor`) para que las vistas no re-deriven el próximo paso.
 *
 * Colores como nombres preset de antd (mismos que `stageMeta`) — el Tag/tema de
 * antd los resuelve, sin hex hardcodeado.
 */

// Etapa del hallazgo (columna `actividad_hallazgo.etapa`) → meta + próximo paso.
// El único hallazgo con acción propia es el DETECTADO ("Presupuestar"); a partir
// de PRESUPUESTADO el próximo paso vive en el presupuesto (ver `accionesPresupuesto`).
export const hallazgoEtapaMeta = {
  DETECTADO: {
    label: 'Detectado',
    color: 'default',
    order: 0,
    nextAction: { key: 'presupuestar', label: 'Presupuestar', tone: 'primary' },
  },
  PRESUPUESTADO: { label: 'Presupuestado', color: 'blue', order: 1, nextAction: null },
  APROBADO: { label: 'Aprobado', color: 'green', order: 2, nextAction: null },
  EN_ODT: { label: 'En ODT', color: 'orange', order: 3, nextAction: null },
  RESUELTO: { label: 'Resuelto', color: 'green', order: 4, nextAction: null },
  DESCARTADO: { label: 'Descartado', color: 'default', order: 5, nextAction: null },
};

// Reexport del mapa de etapas del presupuesto/ODT (8 etapas compuestas), para
// que los consumidores del ciclo tengan un único punto de import.
export const budgetStageMeta = stageMeta;

// Las 5 etapas macro del ciclo — columnas del tablero (Fase 5) y stepper global.
// Mapean el detalle (6 etapas de hallazgo + 8 stages de presupuesto) a la
// narrativa que ve el ejecutivo.
export const CICLO_ETAPAS = [
  { key: 'DETECTADO', label: 'Detectado', hint: 'Hallazgos sin presupuestar' },
  { key: 'PRESUPUESTADO', label: 'Presupuestado', hint: 'Esperando aprobación del cliente' },
  { key: 'APROBADO', label: 'Aprobado', hint: 'Listo para generar la ODT' },
  { key: 'EN_ODT', label: 'En ejecución', hint: 'ODT en terreno' },
  { key: 'RESUELTO', label: 'Resuelto', hint: 'Ciclo cerrado' },
];

/**
 * Próximo paso de una entidad del ciclo, sin que la vista re-derive la lógica.
 * @param {'hallazgo'|'budget'} kind
 * @param {string} key  etapa del hallazgo, o stage del presupuesto/ODT
 * @returns {{key:string,label:string,tone:string}|null}
 */
export function nextActionFor(kind, key) {
  if (kind === 'hallazgo') return hallazgoEtapaMeta[key]?.nextAction ?? null;
  return primaryActionFor(key);
}

// Metadata de UI para la cola accionable (`CicloPage`): de quién es la pelota en
// cada etapa y qué acción de próximo paso ofrece la fila. `condicional` = tuya
// solo si la ODT está sin asignar.
export const CICLO_ETAPA_UI = {
  DETECTADO: { ball: 'tuya', accion: 'presupuestar' },
  PRESUPUESTADO: { ball: 'cliente', accion: null },
  APROBADO: { ball: 'tuya', accion: 'generar_odt' },
  EN_ODT: { ball: 'condicional', accion: 'asignar_odt' },
  RESUELTO: { ball: 'ninguna', accion: null },
};
